import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Badge,
  IconButton,
  Stack,
  useMediaQuery,
  Chip,
  Tooltip,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ShoppingCart,
  LocationOn,
  Bolt,
  Brightness4,
  Brightness7,
  MenuRounded,
  LogoutRounded,
  LoginRounded,
  PersonAddRounded,
  PersonOutlineRounded,
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import { getCurrentUser, isAuthenticated, logout } from '../services/authService';
import LanguageSwitcher from './LanguageSwitcher';
import { useI18n } from '../i18n/I18nProvider';
import { isLiteModeEnabled, setLiteModeEnabled, subscribeLiteMode } from '../utils/liteMode';
import PwaInstallButton from './PwaInstallButton';

const Header = ({ themeMode, toggleTheme }) => {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { cart } = useCart();
  const user = getCurrentUser();
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const [liteMode, setLiteMode] = React.useState(isLiteModeEnabled);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  React.useEffect(() => subscribeLiteMode(setLiteMode), []);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const mobileQuickLinks = user
    ? [
        { label: t('nav_profile', 'Profile'), to: '/profile', icon: <PersonOutlineRounded fontSize="small" /> },
        ...(user.role === 'customer' ? [{ label: t('nav_orders', 'Orders'), to: '/orders' }] : []),
        ...(user.role === 'admin' ? [{ label: t('nav_admin', 'Admin'), to: '/admin' }] : []),
        ...(user.role === 'restaurant' ? [{ label: t('nav_restaurant', 'Restaurant'), to: '/restaurant' }] : []),
        ...(user.role === 'restaurant' || user.role === 'restaurant_supervisor'
          ? [{ label: t('nav_reports', 'Reports'), to: '/restaurant/reports' }]
          : []),
        ...(user.role === 'driver' ? [{ label: t('nav_driver', 'Driver'), to: '/driver' }] : []),
      ]
    : [
        { label: t('nav_login', 'Login'), to: '/login', icon: <LoginRounded fontSize="small" /> },
        { label: t('nav_register', 'Register'), to: '/register', icon: <PersonAddRounded fontSize="small" /> },
      ];

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: theme.palette.background.paper,
        color: 'text.primary',
        borderBottom: themeMode === 'dark'
          ? '1px solid rgba(148, 163, 184, 0.2)'
          : '1px solid rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(16px)',
      }}
    >
      <Toolbar sx={{ py: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
          <Box
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2.5,
                  background:
                    'linear-gradient(135deg, rgba(15,118,110,1) 0%, rgba(14,116,144,0.9) 100%)',
                  boxShadow: '0 10px 20px rgba(15, 118, 110, 0.22)',
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                  }}
                >
                  Skhaftin
                </Typography>
                {!isMobile && (
                  <Typography variant="caption" color="text.secondary">
                    Faster local delivery
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
          {!isMobile && (
            <Chip
              icon={<LocationOn sx={{ fontSize: 18 }} />}
              label="Deliver Ko Kasi"
              variant="outlined"
              sx={{
                bgcolor: theme.palette.background.paper,
                color: 'text.primary',
                borderColor: themeMode === 'dark'
                  ? 'rgba(148, 163, 184, 0.35)'
                  : 'rgba(15, 23, 42, 0.2)',
              }}
            />
          )}
          {!isMobile && (
            <Chip
              label={liteMode ? 'Lite mode on' : 'Lite mode off'}
              variant="outlined"
              onClick={() => setLiteModeEnabled(!liteMode)}
              sx={{
                cursor: 'pointer',
                bgcolor: liteMode ? 'rgba(15,118,110,0.1)' : theme.palette.background.paper,
              }}
            />
          )}
        </Stack>

        {!isMobile && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
            {(user?.role === 'customer' || !user) && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Bolt />}
                component={RouterLink}
                to="/restaurants"
              >
                {t('nav_order_now', 'Order Now')}
              </Button>
            )}
            <Button
              variant="text"
              color="inherit"
              component={RouterLink}
              to="/partner/register"
            >
              {t('nav_partner', 'Become a partner')}
            </Button>
            <PwaInstallButton label="Download App" color="secondary" />
            <LanguageSwitcher compact />
          </Stack>
        )}

        <Box>
          {isMobile && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LanguageSwitcher compact />
              <Tooltip title="Account and app actions">
                <IconButton
                  color="inherit"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open account and app actions"
                >
                  <MenuRounded />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          {!isMobile && (user?.role === 'customer' || !user) && (
            <Button color="inherit" component={RouterLink} to="/restaurants">
              {t('nav_browse', 'Browse')}
            </Button>
          )}
          {!isMobile && user?.role === 'customer' && (
            <Button color="inherit" component={RouterLink} to="/orders">
              {t('nav_orders', 'Orders')}
            </Button>
          )}
          {!isMobile && user && (
            <Button color="inherit" component={RouterLink} to="/profile">
              {t('nav_profile', 'Profile')}
            </Button>
          )}
          {!isMobile && user?.role === 'admin' && (
            <Button color="inherit" component={RouterLink} to="/admin">
              {t('nav_admin', 'Admin')}
            </Button>
          )}
          {!isMobile && user?.role === 'restaurant' && (
            <Button color="inherit" component={RouterLink} to="/restaurant">
              {t('nav_restaurant', 'Restaurant')}
            </Button>
          )}
          {!isMobile && ['restaurant', 'restaurant_supervisor'].includes(user?.role) && (
            <Button color="inherit" component={RouterLink} to="/restaurant/reports">
              {t('nav_reports', 'Reports')}
            </Button>
          )}
          {!isMobile && user?.role === 'driver' && (
            <Button color="inherit" component={RouterLink} to="/driver">
              {t('nav_driver', 'Driver')}
            </Button>
          )}
          <Tooltip title={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              aria-label={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {themeMode === 'light' ? <Brightness4 /> : <Brightness7 />}
            </IconButton>
          </Tooltip>
          {(user?.role === 'customer' || !user) && (
            <IconButton color="inherit" component={RouterLink} to="/cart" aria-label={t('nav_cart', 'Cart')}>
              <Badge color="secondary" badgeContent={itemCount}>
                <ShoppingCart />
              </Badge>
            </IconButton>
          )}
          {isAuthenticated() ? (
            !isMobile && (
              <Button color="inherit" onClick={handleLogout}>
                {t('nav_logout', 'Logout')}
              </Button>
            )
          ) : (
            !isMobile && (
              <>
                <Button color="inherit" component={RouterLink} to="/login">
                  {t('nav_login', 'Login')}
                </Button>
                <Button color="inherit" component={RouterLink} to="/register">
                  {t('nav_register', 'Register')}
                </Button>
              </>
            )
          )}
        </Box>
      </Toolbar>
      <Drawer
        anchor="bottom"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            pb: 2,
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="overline" color="primary.main">
                Account
              </Typography>
              <Typography variant="h6">
                {user ? (user.first_name || user.email || 'Signed in') : 'Sign in or create an account'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use these actions on mobile for login, registration, profile, and logout.
              </Typography>
            </Box>

            <List disablePadding sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {mobileQuickLinks.map((item) => (
                <ListItemButton
                  key={`${item.label}-${item.to}`}
                  component={RouterLink}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.icon ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5, color: 'text.secondary' }}>
                      {item.icon}
                    </Box>
                  ) : null}
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
              <ListItemButton
                component={RouterLink}
                to="/partner/register"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemText primary={t('nav_partner', 'Become a partner')} />
              </ListItemButton>
            </List>

            <Divider />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap>
              <PwaInstallButton fullWidth label="Download App" />
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setLiteModeEnabled(!liteMode);
                  setMobileMenuOpen(false);
                }}
              >
                {liteMode ? 'Lite mode on' : 'Lite mode off'}
              </Button>
              {isAuthenticated() ? (
                <Button
                  fullWidth
                  color="error"
                  variant="contained"
                  startIcon={<LogoutRounded />}
                  onClick={handleLogout}
                >
                  {t('nav_logout', 'Logout')}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  component={RouterLink}
                  to="/login"
                  startIcon={<LoginRounded />}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav_login', 'Login')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
